import axios from 'axios';
import * as Sentry from '@sentry/react';

export const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

type TokenGetter = () => Promise<string | null>;
let _getToken: TokenGetter | null = null;

export function setTokenGetter(getter: TokenGetter) {
  _getToken = getter;
}

/** Get auth headers for raw fetch() calls, such as streaming endpoints that cannot use axios. */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  if (!_getToken) return {};
  const token = await _getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

api.interceptors.request.use(async (config) => {
  if (_getToken) {
    const token = await _getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

let _unauthorizedTimer: ReturnType<typeof setTimeout> | null = null;
function _dispatchUnauthorized() {
  if (!_unauthorizedTimer) {
    _unauthorizedTimer = setTimeout(() => {
      _unauthorizedTimer = null;
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }, 100);
  }
}

const _CB_WINDOW_MS = 30_000;
const _CB_THRESHOLD = 5;
const _CB_COOLDOWN_MS = 15_000;

let _cbErrors: number[] = [];
let _cbOpenUntil = 0;

function _cbRecordError() {
  const now = Date.now();
  _cbErrors.push(now);
  _cbErrors = _cbErrors.filter(t => now - t < _CB_WINDOW_MS);
  if (_cbErrors.length >= _CB_THRESHOLD) {
    _cbOpenUntil = now + _CB_COOLDOWN_MS;
    _cbErrors = [];
    Sentry.captureMessage('API circuit breaker tripped - 5 server errors in 30s', {
      level: 'error',
      tags: { source: 'circuit_breaker' },
    });
  }
}

function _cbIsOpen(): boolean {
  return Date.now() < _cbOpenUntil;
}

function _normalizeApiRoute(rawUrl: unknown): string {
  if (typeof rawUrl !== 'string' || !rawUrl) return 'unknown';
  const path = rawUrl.split('?')[0] || rawUrl;
  return path.replace(/\/\d+(?=\/|$)/g, '/{id}');
}

function _reportInternalApiFailure(error: unknown) {
  if (axios.isCancel(error)) return;
  if (!axios.isAxiosError(error)) return;
  const status = error.response?.status as number | undefined;
  // 405 indicates a real client/server route disagreement. 404 is too noisy in SPAs.
  const shouldReport = !error.response || (status !== undefined && (status >= 500 || status === 405));
  if (!shouldReport) return;

  const route = _normalizeApiRoute(error.config?.url);
  const method = String(error.config?.method || 'GET').toUpperCase();
  const failureKind = !error.response ? 'network' : status && status >= 500 ? 'frontend_api_5xx' : 'contract_drift';
  Sentry.captureMessage('Valentine frontend API failure', {
    level: status && status >= 500 ? 'error' : 'warning',
    tags: {
      source_app: 'valentine-frontend',
      target_app: 'valentine',
      route,
      method,
      status_code: status ?? 'network',
      failure_kind: failureKind,
    },
  });
}

api.interceptors.request.use((config) => {
  const url = config.url || '';
  if (_cbIsOpen() && !url.includes('/health') && !url.includes('/auth/')) {
    return Promise.reject(new axios.CanceledError('API circuit breaker open - backend appears unavailable. Retrying shortly.'));
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    if (status >= 500 || (!error.response && !axios.isCancel(error))) {
      _cbRecordError();
    }

    if (error.response?.status === 401 && _getToken && error.config && !error.config._retried) {
      try {
        const newToken = await _getToken();
        if (newToken) {
          error.config._retried = true;
          error.config.headers.Authorization = `Bearer ${newToken}`;
          return api.request(error.config);
        }
      } catch (refreshErr) {
        Sentry.captureException(refreshErr, { tags: { source: '401_token_retry' } });
      }
      _dispatchUnauthorized();
    } else if (error.response?.status === 401) {
      _dispatchUnauthorized();
    }
    if (error.response?.status === 403 && error.response?.data?.detail === 'Account is deactivated') {
      window.dispatchEvent(new CustomEvent('auth:deactivated'));
    }
    _reportInternalApiFailure(error);
    return Promise.reject(error);
  },
);
